import { useState, useEffect, useRef } from "react";
import { useContacts } from "../hooks/useData";
import { supabase } from "../supabase";
import { Upload, Search, Filter, X, Phone, Clock, ChevronDown } from "lucide-react";


const statusConfig = {
  active:  { label: "Actif",    color: "bg-green-100 text-green-800" },
  pending: { label: "En attente", color: "bg-amber-100 text-amber-800" },
  inactive:{ label: "Inactif",  color: "bg-gray-100 text-gray-600" },
  hot:     { label: "Chaud",    color: "bg-orange-100 text-orange-800" },
  warm:    { label: "Tiède",    color: "bg-amber-100 text-amber-800" },
  cold:    { label: "Froid",    color: "bg-blue-100 text-blue-800" },
};

const sourceConfig = {
  auto:     { label: "WhatsApp", color: "bg-green-500" },
  import:   { label: "Import",   color: "bg-blue-500" },
  whatsapp: { label: "WhatsApp", color: "bg-green-500" },
  instagram:{ label: "Instagram",color: "bg-pink-500" },
  facebook: { label: "Facebook", color: "bg-blue-600" },
};

function timeAgo(dateStr) {
  if (!dateStr) return "Jamais";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 60000);
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `Il y a ${diff}min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
  return `Il y a ${Math.floor(diff / 1440)}j`;
}

function ContactDrawer({ contact, onClose, onUpdate }) {
  const [name, setName] = useState(contact.name || '');
  const [email, setEmail] = useState(contact.email || '');
  const [notes, setNotes] = useState(contact.notes || '');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(contact.tags || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const { data } = await supabase
        .from('crm_contacts')
        .update({ name, email, notes, tags, updated_at: new Date() })
        .eq('id', contact.id)
        .select()
        .single();
      onUpdate(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  function addTag(e) {
    if (e.key === 'Enter' && tagInput.trim()) {
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  }

  function removeTag(tag) {
    setTags(tags.filter(t => t !== tag));
  }

  const status = statusConfig[contact.status] || statusConfig.active;
  const source = sourceConfig[contact.source] || sourceConfig.auto;
  const initials = (contact.name || contact.phone || '??').slice(0, 2).toUpperCase();

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between z-10">
          <span className="text-sm font-medium text-gray-800">Fiche contact</span>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Avatar + infos de base */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center text-lg font-medium text-orange-800 flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>
                  {status.label}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full text-white ${source.color}`}>
                  {source.label}
                </span>
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                <Phone size={11} />
                <span>{contact.phone}</span>
              </div>
              {contact.last_interaction_at && (
                <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-400">
                  <Clock size={11} />
                  <span>Dernière interaction : {timeAgo(contact.last_interaction_at)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-lg font-medium text-gray-800">{contact.total_orders || 0}</div>
              <div className="text-xs text-gray-500">Commandes</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-lg font-medium text-orange-600">
                {(contact.total_spent || 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Dépensé</div>
            </div>
          </div>

          {/* Formulaire */}
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Nom</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Nom du contact"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Email</label>
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="email@exemple.com"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Tags <span className="text-gray-400 font-normal">(Entrée pour ajouter)</span>
              </label>
              <div className="border border-gray-200 rounded-lg p-2 focus-within:border-orange-400">
                <div className="flex flex-wrap gap-1 mb-1">
                  {tags.map(tag => (
                    <span key={tag} className="flex items-center gap-1 bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">
                      {tag}
                      <button onClick={() => removeTag(tag)}><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={addTag}
                  placeholder="Ajouter un tag..."
                  className="w-full text-sm outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Notes sur ce contact..."
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400 resize-none"
              />
            </div>
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="w-full py-2.5 bg-orange-600 text-white rounded-xl text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
          >
            {saving ? 'Sauvegarde...' : saved ? '✓ Sauvegardé !' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ orgId, onClose, onImported }) {
  const [step, setStep] = useState('upload');
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  function parseCSV(text) {
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    return lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
      return obj;
    }).filter(r => r.phone || r.téléphone || r.tel);
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCSV(ev.target.result);
      setPreview(parsed.slice(0, 5));
      setStep('preview');
    };
    reader.readAsText(file, 'UTF-8');
  }

  async function doImport() {
    const file = fileRef.current?.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const contacts = parseCSV(text);
      const response = await fetch('https://smart-crm-production.up.railway.app/api/import-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId, contacts })
      });
      const data = await response.json();
      setResult(data.results);
      setStep('done');
      onImported();
    } catch (e) {
      console.error(e);
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">Importer des contacts</span>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="p-5">

          {step === 'upload' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                <Upload size={24} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-1">Glissez votre fichier CSV ici</p>
                <p className="text-xs text-gray-400 mb-3">Colonnes attendues : phone, name, email, tags, notes</p>
                <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} className="hidden" id="csvFile" />
                <label htmlFor="csvFile" className="cursor-pointer px-4 py-2 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700">
                  Choisir un fichier
                </label>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs font-medium text-gray-700 mb-1">Format CSV attendu :</p>
                <code className="text-xs text-gray-500">phone,name,email,tags,notes</code><br/>
                <code className="text-xs text-gray-400">+212600000000,Ahmed,ahmed@mail.com,vip,Client fidèle</code>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Aperçu des 5 premiers contacts :</p>
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                {preview.map((c, i) => (
                  <div key={i} className="px-3 py-2 border-b border-gray-50 last:border-0 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-xs font-medium text-orange-800 flex-shrink-0">
                      {(c.name || c.phone || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-800">{c.name || c.nom || '—'}</div>
                      <div className="text-xs text-gray-400">{c.phone || c.téléphone || c.tel}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep('upload')} className="flex-1 py-2 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50">
                  Annuler
                </button>
                <button onClick={doImport} disabled={importing} className="flex-1 py-2 bg-orange-600 text-white text-sm rounded-xl hover:bg-orange-700 disabled:opacity-50">
                  {importing ? 'Import...' : 'Importer'}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && result && (
            <div className="text-center space-y-4 py-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl">✓</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800 mb-2">Import terminé !</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-green-50 rounded-lg p-2 text-center">
                    <div className="text-lg font-medium text-green-700">{result.success}</div>
                    <div className="text-xs text-green-600">Importés</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <div className="text-lg font-medium text-amber-700">{result.skipped}</div>
                    <div className="text-xs text-amber-600">Ignorés</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2 text-center">
                    <div className="text-lg font-medium text-red-700">{result.errors}</div>
                    <div className="text-xs text-red-600">Erreurs</div>
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="w-full py-2 bg-orange-600 text-white text-sm rounded-xl hover:bg-orange-700">
                Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Contacts() {
  const { contacts: legacyContacts} = useContacts();
  const [crmContacts, setCrmContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    async function init() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }
      const { data: orgData } = await supabase
        .from('organizations')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (orgData?.id) {
        setOrgId(orgData.id);
        await loadCRMContacts(orgData.id);
      }
      setLoading(false);
    }
    init();
  }, []);

  async function loadCRMContacts(id) {
    try {
      const { data } = await supabase
        .from('crm_contacts')
        .select('*')
        .eq('org_id', id)
        .order('last_interaction_at', { ascending: false });
      setCrmContacts(data || []);
    } catch (e) {
      console.error(e);
    }
  }

  function handleUpdate(updated) {
    setCrmContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
  }

  // Fusionner contacts legacy + CRM
  const allContacts = crmContacts.length > 0 ? crmContacts : legacyContacts.map(c => ({
    id: c.id,
    phone: c.phone,
    name: c.name || null,
    status: c.lead_status || 'cold',
    source: c.source || 'auto',
    last_interaction_at: c.last_interaction,
    total_orders: 0,
    total_spent: 0,
    tags: [],
    notes: null,
  }));

  const filtered = allContacts.filter(c => {
    const matchSearch = !search ||
      (c.phone || '').includes(search) ||
      (c.name || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchSource = filterSource === 'all' || c.source === filterSource;
    return matchSearch && matchStatus && matchSource;
  });

  const stats = {
    total: allContacts.length,
    active: allContacts.filter(c => c.status === 'active' || c.status === 'hot').length,
    imported: allContacts.filter(c => c.source === 'import').length,
    auto: allContacts.filter(c => c.source === 'auto').length,
  };

  return (
    <div className="space-y-4">

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total contacts", value: stats.total, color: "text-gray-800" },
          { label: "Actifs", value: stats.active, color: "text-green-600" },
          { label: "Via WhatsApp", value: stats.auto, color: "text-orange-600" },
          { label: "Importés", value: stats.imported, color: "text-blue-600" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className={`text-xl font-medium ${s.color}`}>
              {loading ? "..." : s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Barre actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-0 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou téléphone..."
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400"
          />
        </div>
        <button
          onClick={() => setShowFilter(!showFilter)}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50"
        >
          <Filter size={14} />
          Filtrer
          <ChevronDown size={12} />
        </button>
        <button
          onClick={() => setShowImport(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white rounded-xl text-sm hover:bg-orange-700"
        >
          <Upload size={14} />
          Importer CSV
        </button>
      </div>

      {/* Filtres */}
      {showFilter && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex flex-wrap gap-4">
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">Statut</div>
            <div className="flex gap-1.5 flex-wrap">
              {['all', 'active', 'pending', 'inactive'].map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    filterStatus === s
                      ? 'bg-orange-100 text-orange-700 border-orange-300'
                      : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {s === 'all' ? 'Tous' : s === 'active' ? 'Actif' : s === 'pending' ? 'En attente' : 'Inactif'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">Source</div>
            <div className="flex gap-1.5 flex-wrap">
              {['all', 'auto', 'import'].map(s => (
                <button
                  key={s}
                  onClick={() => setFilterSource(s)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    filterSource === s
                      ? 'bg-orange-100 text-orange-700 border-orange-300'
                      : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {s === 'all' ? 'Toutes' : s === 'auto' ? 'WhatsApp' : 'Import CSV'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">
            {filtered.length} contact{filtered.length !== 1 ? 's' : ''}
          </span>
          {search || filterStatus !== 'all' || filterSource !== 'all' ? (
            <button
              onClick={() => { setSearch(''); setFilterStatus('all'); setFilterSource('all'); }}
              className="text-xs text-orange-600 hover:text-orange-700"
            >
              Réinitialiser filtres
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            {search ? 'Aucun résultat' : 'Aucun contact — importez un CSV ou attendez les premiers messages'}
          </div>
        ) : (
          filtered.map((c, i) => {
            const status = statusConfig[c.status] || statusConfig.active;
            const source = sourceConfig[c.source] || sourceConfig.auto;
            const initials = (c.name || c.phone || '??').slice(0, 2).toUpperCase();

            return (
              <div
                key={i}
                onClick={() => setSelectedContact(c)}
                className="px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-sm font-medium text-orange-800">
                      {initials}
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 ${source.color} rounded-full border-2 border-white`}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {c.name || c.phone}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-400">{c.name ? c.phone : source.label}</span>
                      {c.tags?.length > 0 && (
                        <div className="flex gap-1">
                          {c.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="text-xs bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full">
                              {tag}
                            </span>
                          ))}
                          {c.tags.length > 2 && (
                            <span className="text-xs text-gray-400">+{c.tags.length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {timeAgo(c.last_interaction_at)}
                    </div>
                  </div>
                  {(c.total_orders > 0) && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs font-medium text-orange-600">{c.total_orders} cmd</div>
                      <div className="text-xs text-gray-400">{(c.total_spent || 0).toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Drawer fiche contact */}
      {selectedContact && (
        <ContactDrawer
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onUpdate={handleUpdate}
        />
      )}

      {/* Modal import CSV */}
      {showImport && (
        <ImportModal
          orgId={orgId}
          onClose={() => setShowImport(false)}
          onImported={() => orgId && loadCRMContacts(orgId)}
        />
      )}

    </div>
  );
}